import * as types from '../mutation-types'
import logger from "../../lib/logger";


const state = {
  items: [],
  full_cart: [],
  provider_order: null,
  service_class_order: null,
  billable_rate_type_order: null
}


const helpers = {
  monthly: 'monthly',
  rate_total(rate_period, xmission_only, rate_deferred, rate_terms = ['all']) {
    // filter only desired items by rate period
    let items = state.items;
    if (rate_period !== null) {
      items = state.items.filter(x => x.rate_period === rate_period);
    }
    if (!rate_terms.includes('all')) {
      items = items.filter(x => rate_terms.includes(x.rate_term));
    }
    // sometimes we only want to sum xmission-billed items
    items = xmission_only ? items.filter(x => x.provider.name === 'xmission') : items;
    // sometimes we only want payment required now or not
    if (rate_deferred !== null) {
      items = items.filter(x => x.rate_deferred === rate_deferred);
    }
    // group items by item group since we need to sum along each item group
    let item_groups = {};
    let total_has_taxes = false;
    for (let item of items) {
      if (item.taxable) {
        total_has_taxes = true;
      }
      if (!(item.item_group in item_groups)) {
        item_groups[item.item_group] = [];
      }
      item_groups[item.item_group].push(item);
    }
    let total = 0.;
    // sum over each item group, then add to the total
    for (let item_group of Object.keys(item_groups)) {
      // sum all relevant item rates, taking care to make promo's negative
      let group_total = item_groups[item_group].reduce((partialSum, item) => partialSum + Number(item.rate * item.quantity) * (item.rate_type === 'promo' ? -1 : 1), 0);
      // ensure we don't have negative totals
      group_total = Math.max(group_total, 0);
      total += group_total;
    }
    return {'total': total, 'taxed': total_has_taxes};
  },

  all_providers() {
    let providers = [... new Set(state.items.map(x => {return x.provider.name}))].sort();
    logger.debug('providers', providers)
    if (providers.includes('xmission')) {
      providers = ['xmission'].concat(providers.filter(x => x !== 'xmission'));
    }
    logger.debug('providers after reorder', providers)
    return providers;
  },

  all_service_classes() {
    return [... new Set(state.items.map(x => {return x.service_class}))].sort();
  },

  sort_by_index(item_a, item_b, order, key) {
    return order.indexOf(item_a[key]) - order.indexOf(item_b[key]);
  },

  sort_billable_items(item_a, item_b, provider_order, service_class_order, billable_rate_type_order) {
    // first order by provider
    let provider_sort = helpers.sort_by_index(item_a.provider, item_b.provider, provider_order, 'name');
    if (provider_sort !== 0) {
      return provider_sort;
    }

    // now order by service type
    let service_class_sort = helpers.sort_by_index(item_a, item_b, service_class_order, 'service_class');
    if (service_class_sort !== 0) {
      return service_class_sort;
    }
    // now order by billable item rate type
    let billable_rate_type_sort = helpers.sort_by_index(item_a, item_b, billable_rate_type_order, 'rate_type');
    if (billable_rate_type_sort !== 0) {
      return billable_rate_type_sort;
    }

    // lastly order by description
    return item_a.description.localeCompare(item_b.description);
  },
  getCleanCart(item_type) {
    let item_names_to_remove   = [];
    let item_name_map = {};
    for (let billable_item of state.items) {
      item_name_map[billable_item.name] = billable_item;
    }

    function findRootItemType (item, height) {
      if (item.item_type === item_type){
        item_names_to_remove.push(item.name);
      }
      if (item.related_item === '') {
        return {has_root_item_type: item.item_type === item_type, height: height};
      }
      if (height > 10) {
        logger.error('circular reference detected', item)
        return {has_root_item_type: item.item_type === item_type, height: height};
      }
      let root_item_type = findRootItemType(item_name_map[item.related_item], height + 1);
      if (root_item_type.has_root_item_type) {
        item_names_to_remove.push(item.name);
      }
      return {has_root_item_type: root_item_type.has_root_item_type, height: root_item_type.height + 1};
    }

    for (let item of state.items) {
      findRootItemType(item, 0);
    }

    return state.items.filter(x => !item_names_to_remove.includes(x.name));
  },
  getFullCart(state) {
        // get all billable items and useful maps
    let all_billable_items = state.items;
    let item_name_map = {};
    for (let billable_item of all_billable_items) {
      item_name_map[billable_item.name] = billable_item;
    }

    // split by related and unrelated since each will be sorted differently
    let related_items = all_billable_items.filter(x => x.related_item !== '');
    let unrelated_items = all_billable_items.filter(x => x.related_item === '');

    // get any custom or default orderings
    let provider_order = state.provider_order !== null ? state.provider_order : helpers.all_providers();
    let service_class_order = state.service_class_order !== null ? state.service_class_order : helpers.all_service_classes();
    let billable_rate_type_order = state.billable_rate_type_order !== null ? state.billable_rate_type_order : ['price', 'promo', 'fee'];

    // sort unrelated items
    unrelated_items = unrelated_items.sort((a, b) => { return helpers.sort_billable_items(a, b, provider_order, service_class_order, billable_rate_type_order); });

    // inject related items into the sorted array

    // first get all distances of related items to root item (necessary since related items may point to a related item)
    function findDistanceToRootItem (item, height) {
      if (item.related_item === '') {
        return 0;
      }
      if (height > 10) {
        logger.error('circular reference detected', item)
        return 0;
      }
      return 1 + findDistanceToRootItem(item_name_map[item.related_item], height + 1);
    }

    let distance_map = {};
    for (let related_item of related_items) {
      let item_distance = String(findDistanceToRootItem(related_item, 0));
      logger.debug('item distance', item_distance, related_item)
      if (!(item_distance in distance_map)) {
        distance_map[item_distance] = [];
      }
      distance_map[item_distance].push(related_item);
    }

    // now iterate by distance (smallest first) and inject items, only works assuming there are no directed cycles
    for (let distance of Object.keys(distance_map).sort((a, b) => {return Number(a) - Number(b)})) {
      for (let related_item of distance_map[distance]) {
        let inject_ndx = unrelated_items.findIndex(x => x.name === related_item.related_item);
        logger.debug('inject_ndx', inject_ndx, related_item)
        // inject it right after the related item
        unrelated_items.splice(inject_ndx + 1, 0, related_item);
      }
    }

    logger.debug('all billable items', all_billable_items)
    logger.debug('full cart', unrelated_items)
    return unrelated_items;
  }
}


const getters = {
  getFullCart(){
    return state.full_cart;
  },
  oneTimeXmissionTotalDueNow() {
    return helpers.rate_total(null, true, false);
  },
  oneTimeXmissionTotalDueLater() {
    return helpers.rate_total(null, true, true);
  },
  monthlyXmissionTotals(){
    // first divide all monthly items by rate term. This is necessary since we need to sum after each rate term to
    // produce a list of totals, ordered by rate term with the null term last.
    let rate_terms = [... new Set(state.items.map(x => x.rate_term))].sort();
    let totals = [];
    for (let i = 0; i < rate_terms.length; i++) {
      let total_details = helpers.rate_total(null, true, null, rate_terms.slice(i, rate_terms.length))
      totals.push({rate_term: rate_terms[i], total: total_details['total'], taxed: total_details['taxed'] });
    }
    return totals;
  },
}

const mutations = {
  [types.ADD_TO_CART] (state, {name, description, item_type, item_group,
    provider, rate, rate_type, rate_period, rate_term, rate_mode, rate_min, rate_max,
    rate_deferred, quantity, related_item, service_class, taxable}) {

    // remove any existing items with the same item_type, this
    // simplifies changing features as a customer clicks
    state.items = helpers.getCleanCart(item_type);

    state.items.push({
      name, description, item_type, item_group, provider,
      rate, rate_type, rate_period, rate_term, rate_mode, rate_min, rate_max,
      rate_deferred, quantity, related_item, service_class, taxable});
    },
  [types.REMOVE_FROM_CART] (state, item_type) {
    state.items = helpers.getCleanCart(item_type);
  },
  [types.CALCULATE_CART] (state) {
    state.full_cart = helpers.getFullCart(state);
  },
  [types.EMPTY_CART] (state) {
    state.items = [];
  },
  [types.SET_PROVIDER_ORDER] (state, provider_order) {
    state.provider_order = provider_order;
  },
  [types.SET_SERVICE_CLASS_ORDER] (state, service_class_order) {
    state.service_class_order = service_class_order;
  },
  [types.SET_BILLABLE_ITEM_TYPE_ORDER] (state, billable_rate_type_order) {
    state.billable_rate_type_order = billable_rate_type_order;
  },
}


const actions = {

}


export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/cart.js